import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ApplyTagsFunction, TagsAppliedFunction } from '../../StudyList';
import { ToolbarOptions } from '../StudyListToolbar';
import { CriteriaSidebar } from './CriteriaSidebar';
import { HighlightsSidebar } from './HighlightsSidebar';
import { SortAndFilterSidebar } from './SortAndFilterSidebar';
import { TagsSidebar } from './TagsSidebar';
import type { FilterSortDisplayForm } from './useFilterSortDisplayForm';
import { EditableCriteriaHandle } from 'components/shared/EditableCriteria';
import { useStudyListLayout } from 'pages/StudyList/StudyListLayoutContext';

export type SidebarName = 'criteria' | 'highlights' | 'sortAndFilter' | 'tags';

type ToggleProps = {
  disclosureId: string;
  toggleRef: React.RefObject<HTMLButtonElement>;
};

type Props = {
  currentSidebar: SidebarName | null;
  reviewId?: string;
  criteria: ToggleProps & {
    editableCriteriaRef: React.RefObject<EditableCriteriaHandle>;
    showEmptyState: boolean;
    close: () => void;
    onCloseConfirmed: () => void;
  };
  highlights: ToggleProps & {
    defaultShowAbstracts: boolean;
    toggleAbstracts: () => void;
    toggleHighlights: () => void;
    enableHighlights: () => void;
  };
  sortAndFilter: ToggleProps & {
    form: UseFormReturn<FilterSortDisplayForm>;
    sortOptions: Record<string, string>;
    isAwaitingOtherReviewer: boolean;
    reviewStage: ToolbarOptions['reviewStage'];
    isResetting: boolean;
    onApply: (values: FilterSortDisplayForm) => Promise<void>;
    onReset: () => Promise<void>;
  };
  tags: ToggleProps & {
    selectedStudyIds: number[];
    onApplyTags: ApplyTagsFunction;
    onTagsApplied: TagsAppliedFunction;
  };
  closeSidebar: (name: Exclude<SidebarName, 'criteria'>) => void;
  focusFallbackRef: React.RefObject<HTMLElement>;
};

export const SidebarPanel = ({
  currentSidebar,
  reviewId,
  criteria,
  highlights,
  sortAndFilter,
  tags,
  closeSidebar,
  focusFallbackRef,
}: Props) => {
  const { sidebarPanelRoot: contextPanelRoot } = useStudyListLayout();

  // Backbone pages render the panel in haml, so fall back to finding it by id
  const sidebarPanelRoot =
    contextPanelRoot ?? document.getElementById('studyListSidebarPanel');
  if (!sidebarPanelRoot) {
    return null;
  }

  const focusToggleAfterClose = (
    toggleRef: React.RefObject<HTMLButtonElement>,
    disclosureId: string
  ) => () => {
    const focused = document.activeElement;
    const closingPanel = document.getElementById(disclosureId);
    const hasFocusMovedElsewhere =
      focused && focused !== document.body && !closingPanel?.contains(focused);
    if (hasFocusMovedElsewhere) return;

    // The toggle button could unmount while open (applying tags clears the selection,
    // a vote removes the study). The sticky toolbar is a target that won't scroll.
    if (!toggleRef.current?.isConnected) {
      focusFallbackRef.current
        ?.querySelector<HTMLElement>('button, input')
        ?.focus();
      return;
    }

    toggleRef.current?.focus();
  };

  const sharedSidebarProps = (
    name: SidebarName,
    { toggleRef }: ToggleProps,
    disclosureId: string
  ) => ({
    isOpen: currentSidebar === name,
    onClosed: focusToggleAfterClose(toggleRef, disclosureId),
    panelRoot: sidebarPanelRoot,
  });

  return (
    <>
      <CriteriaSidebar
        {...criteria}
        {...sharedSidebarProps('criteria', criteria, criteria.disclosureId)}
        onClose={criteria.close}
        reviewId={reviewId}
      />
      <HighlightsSidebar
        {...highlights}
        {...sharedSidebarProps(
          'highlights',
          highlights,
          highlights.disclosureId
        )}
        onClose={() => closeSidebar('highlights')}
      />
      <SortAndFilterSidebar
        {...sortAndFilter}
        {...sharedSidebarProps(
          'sortAndFilter',
          sortAndFilter,
          sortAndFilter.disclosureId
        )}
        onClose={() => closeSidebar('sortAndFilter')}
      />
      <TagsSidebar
        {...tags}
        {...sharedSidebarProps('tags', tags, tags.disclosureId)}
        onClose={() => closeSidebar('tags')}
        reviewId={reviewId}
      />
    </>
  );
};
